import { useEffect, useMemo } from 'react';
import { useCookie } from 'react-use';
import uniqueid from 'uniqid';
import { z } from 'zod';
import { AuthState, useIdentify } from '@hungryroot/customer';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { type SaladShooterAction } from '../actions';
import { logSaladShooterIngest, trackNoopCalled } from '../logs';

const SALAD_SHOOTER_ORIGIN = z
  .string()
  .parse(process.env.PUBLIC_SALAD_SHOOTER_ORIGIN);
export const INGESTION_ROUTE = `${SALAD_SHOOTER_ORIGIN}/ev`;

const SALAD_SHOOTER_STREAM_ID_COOKIE_NAME = 'hr.salad-shooter.stream-id';

export type EvPayload = {
  id: string;
  timestamp: Date;
  origin: 'hr-web'; // constant
  stream_id: string;
  data: Array<
    Pick<SaladShooterAction, 'kind' | 'action'> & {
      href: string;
      when: Date;
      customer_id: number | null;
    }
  >;
};

export const noop = () => {
  trackNoopCalled();
};

export function useSaladShooter() {
  const [streamIdCookieValue, setCookie] = useCookie(
    SALAD_SHOOTER_STREAM_ID_COOKIE_NAME
  );
  const identify = useIdentify();
  const featureFlag = useFeatureFlag('web.feature-flag.salad-shooter.enabled');

  const streamId = useMemo(() => {
    if (streamIdCookieValue == null) {
      return uniqueid('stream-id');
    }
    return streamIdCookieValue;
  }, [streamIdCookieValue]);

  useEffect(() => {
    if (streamIdCookieValue === null) {
      setCookie(streamId);
    }
  }, [streamIdCookieValue, streamId, setCookie]);

  if (identify.authState === AuthState.isLoading || featureFlag !== true) {
    return {
      track: noop,
    };
  }

  const customerId =
    identify.authState === AuthState.isAuthenticated
      ? identify.customerData.id
      : null;

  return {
    track: async function track(events: SaladShooterAction[]) {
      const payload: EvPayload = {
        id: uniqueid('request-id'),
        timestamp: new Date(),
        origin: 'hr-web',
        stream_id: streamId,
        data: events.map((event) => {
          return {
            ...event,
            href: window.location.href,
            when: new Date(),
            customer_id: customerId,
          };
        }),
      };

      logSaladShooterIngest(INGESTION_ROUTE, payload);

      // Intentionally not awaited, we don't want to block the main thread
      fetch(INGESTION_ROUTE, {
        method: 'POST',
        body: JSON.stringify(payload),
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      });
    },
  };
}
